import { Tag } from 'antd';
import styled from 'styled-components';

export {
  BoldSpanMidSize,
  BoldSpanLargeSize,
  ColoredBoldSpanLargeSize,
  SSOLoginSpan,
  StrongTitleSpan,
} from '@monorepo/shared-lib';

interface StrongTagProps {
  $smaller?: boolean;
  onClick?: () => void;
}

interface StrongTagDisabledProps {
  $smaller?: boolean;
}

interface GreenSpanProps {
  fontSize?: string;
  fontWeight?: string;
  $lineHeight?: string;
}

interface BoldNameSpanProps {
  fontWeight?: string;
}

interface GrayItalicSpanProps {
  fontSize?: string;
  $lineHeight?: string;
  color?: string;
}

interface CardDescGreenSpanProps {
  fontSize?: string;
  fontWeight?: string;
}

interface CardDescBoldSpanProps {
  fontSize?: string;
  fontWeight?: string;
  $lineHeight?: string;
}

interface TableItemSpanProps {
  fontSize?: string;
  fontWeight?: string;
  $lineHeight?: string;
  fontColor?: string;
}

interface ListItemSpanProps {
  fontSize?: string;
  fontWeight?: string;
  $lineHeight?: string;
}

/** Home dashboard metric cards — label above the big number. */
export const DashboardCardLabel = styled.span`
  font-family: var(--font-family);
  font-size: var(--font-size-base);
  color: var(--color-text-muted);
  display: block;
  margin-bottom: 8px;
`;

/** Home dashboard metric cards — primary numeric value. */
export const DashboardCardValue = styled.span`
  font-family: var(--font-family);
  font-size: 32px;
  font-weight: 600;
  color: var(--color-text-dark);
`;

export const StrongTag = styled(Tag)<StrongTagProps>`
  font-family: Inter;
  font-size: ${(props) => (props.$smaller ? '10px' : '12px')};
  font-weight: 500;
  line-height: ${(props) => (props.$smaller ? '10px' : '12px')};
  text-align: left;
  background: var(--color-surface-raised, #ffffff);
  padding: ${(props) => (props.$smaller ? '4px 6px' : '6px 8px')};
  color: var(--color-text-base, var(--color-text-dark, #042a2a));
  border: 1px solid var(--color-border, #dbeae3);

  &:hover {
    cursor: ${(props) => (props.onClick ? 'pointer' : 'default')};
    text-decoration: none;
  }
`;

export const StrongTagDisabled = styled.div<StrongTagDisabledProps>`
  font-family: Inter;
  font-size: ${(props) => (props.$smaller ? '10px' : '13px')};
  font-weight: 400;
  line-height: ${(props) => (props.$smaller ? '10px' : '12px')};
  text-align: left;
  //background: var(--color-surface-raised, #ffffff);
  padding: ${(props) => (props.$smaller ? '4px 6px' : '6px 8px')};
  color: var(--color-text-dark, #042a2a);
  //border: 1px solid #ffffff;

  //&:hover {
  //  cursor: ${(props) => (props.onClick ? 'pointer' : 'default')};
  //}
`;

interface ColoredTagProps extends StrongTagProps {
  bgcolor?: string;
  bordercolor?: string;
  icon?: React.ReactNode;
}

/** Custom colours only — do not forward `color` to antd Tag (it applies its own text/contrast rules). */
export const ColoredTag = styled(StrongTag).withConfig({
  shouldForwardProp: (prop) =>
    !['bgcolor', 'bordercolor', 'color'].includes(String(prop)),
})<ColoredTagProps>`
  color: ${(props) => (props.color ? props.color : 'var(--color-text-base, var(--color-text-dark, #042a2a))')} !important;
  background-color: ${(props) =>
    props.bgcolor ? props.bgcolor : 'var(--color-surface, #ffffff)'} !important;
  border-color: ${(props) =>
    props.bordercolor ? props.bordercolor : 'var(--color-border, transparent)'} !important;

  .anticon {
    color: inherit !important;
  }
`;

export const GreenSpan = styled.span<GreenSpanProps>`
  font-family: Inter;
  font-size: ${(props) => (props.fontSize ? props.fontSize : '10px')};
  font-weight: ${(props) => (props.fontWeight ? props.fontWeight : '400')};
  line-height: ${(props) => (props.$lineHeight ? props.$lineHeight : '24px')};
  text-align: left;
  color: var(--color-primary, #41aaaf);
`;

export const DashboardContainer = styled.div`
  width: 24%;
  min-width: 266px;
  height: 165px;
  border-radius: 10px;
  border: 1px solid var(--color-border, #dbeae3);
  box-shadow: 0px 0px 10px 0px #0000000d;
  padding: 30px 40px;
  background: var(--color-surface-raised, transparent);
  color: var(--color-text-base, var(--color-text-dark, #042a2a));

  &:hover {
    border: 1px solid var(--color-primary, #41aaaf);
    box-shadow: 0px 0px 10px 0px #0000000d;
    cursor: pointer;
    .container-icon {
      color: var(--color-primary, #41aaaf);
    }
  }

  .container-icon {
    float: inline-end;
    width: 24px;
    height: 24px;
    color: var(--color-text-base, var(--color-text-dark, #042a2a));
  }

  .container-title {
    font-weight: 400;
    font-size: 16px;
    line-height: 24px;
    color: var(--color-text-base, var(--color-text-dark, #042a2a));
  }

  .container-number {
    font-size: 40px;
    font-weight: 400;
    line-height: 40px;
    text-align: left;
    color: var(--color-text-base, var(--color-text-dark, #042a2a));
  }

  .container-number-title-span {
    font-family: var(--font-family, Inter, sans-serif);
    font-size: 10px;
    font-weight: 400;
    line-height: 24px;
    text-align: left;
    color: var(--color-text-muted, #5a6a6a);
  }
`;

export const BoldNameSpan = styled.span<BoldNameSpanProps>`
  font-size: 13px;
  font-weight: ${(props) => (props.fontWeight ? props.fontWeight : '600')};
  line-height: 18px;
  text-align: left;
  color: var(--color-text-dark, #042a2a);
`;

export const GraySpan = styled.span`
  font-family: Inter;
  font-size: 12px;
  font-weight: 400;
  text-align: left;
  color: var(--color-text-muted, #5a6a6a);
`;

export const BlackSpan = styled.span`
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  text-align: left;
  color: var(--color-text-dark, #042a2a);
`;

export const CardTitleSpan = styled.span`
  font-family: Inter;
  font-size: 18px;
  font-weight: 400;
  line-height: 24px;
  text-align: left;
  color: var(--color-text-base, #2e2e2e);
`;

export const CardTitleLargeSpan = styled.span`
  font-family: Inter;
  font-size: 20px;
  font-weight: 400;
  text-align: left;
  color: var(--color-text-base, #2e2e2e);
`;

export const CardDescSpan = styled.span`
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
  text-align: left;
  color: var(--color-text-muted, #5a6a6a);
`;

export const CardListSpan = styled.span`
  font-family: Inter;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  text-align: left;
  color: var(--color-text-muted, #5a6a6a);
`;

export const GrayItalicSpan = styled.span<GrayItalicSpanProps>`
  font-family: Inter;
  font-size: ${(props) => (props.fontSize ? props.fontSize : '12px')};
  font-style: italic;
  font-weight: 400;
  line-height: ${(props) => (props.$lineHeight ? props.$lineHeight : '24px')};
  text-align: left;
  color: ${(props) =>
    props.color ? props.color : 'var(--color-text-muted, #5a6a6a)'};
`;

export const CardDescGreenSpan = styled.span<CardDescGreenSpanProps>`
  color: var(--color-primary, #41aaaf);
  font-family: Inter;
  font-size: ${(props) => (props.fontSize ? props.fontSize : '24px')};
  font-weight: ${(props) => (props.fontWeight ? props.fontWeight : '600')};
  line-height: 26px;
  text-align: left;
`;

export const CardDescBoldSpan = styled.span<CardDescBoldSpanProps>`
  color: var(--color-text-base, #2e2e2e);
  font-family: Inter;
  font-size: ${(props) => (props.fontSize ? props.fontSize : '18px')};
  font-weight: ${(props) => (props.fontWeight ? props.fontWeight : '600')};
  line-height: ${(props) => (props.$lineHeight ? props.$lineHeight : '34px')};
  text-align: left;
`;

export const CardDescBoldGraySpan = styled.span`
  color: var(--color-text-muted, #5a6a6a);
  font-family: Inter;
  font-size: 16px;
  font-weight: 600;
  line-height: 34px;
  text-align: left;
`;

export const TableTitleSpan = styled.span`
  font-family: Inter;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.05em;
  color: var(--color-text-muted, #5a6a6a);
`;

export const TableItemSpan = styled.span<TableItemSpanProps>`
  font-family: Inter;
  font-size: ${(props) => (props.fontSize ? props.fontSize : '12px')};
  font-weight: ${(props) => (props.fontWeight ? props.fontWeight : '400')};
  line-height: ${(props) => (props.$lineHeight ? props.$lineHeight : '30px')};
  text-align: left;
  color: ${(props) => (props.fontColor ? props.fontColor : 'var(--color-text-base, #2e2e2e)')};
`;

export const ListItemSpan = styled.span<ListItemSpanProps>`
  font-family: Inter;
  font-size: ${(props) => (props.fontSize ? props.fontSize : '12px')};
  font-weight: ${(props) => (props.fontWeight ? props.fontWeight : '400')};
  line-height: ${(props) => (props.$lineHeight ? props.$lineHeight : '18px')};
  text-align: left;
  color: var(--color-text-muted, #5a6a6a);
`;

export const GreenLabel = styled.div.attrs((props) => ({
  className: ['pricing-most-popular-label', props.className]
    .filter(Boolean)
    .join(' '),
}))`
  font-family: Inter;
  font-size: 15px;
  top: -40px;
  position: absolute;
  left: -2px;
  background-color: var(--color-primary, #41aaaf);
  width: 101%;
  height: 39px;
  align-content: center;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  color: #052325 !important;

  &:after {
    content: '';
    position: absolute;
    border: 10px solid transparent;
    border-left: 10px solid var(--color-primary, #41aaaf);
    bottom: -8px;
    left: 0px;
  }

  &:before {
    content: '';
    position: absolute;
    border: 10px solid transparent;
    border-right: 10px solid var(--color-primary, #41aaaf);
    bottom: -8px;
    right: 0px;
  }

  @media (max-width: 1080px) {
    width: 103%;
    #star-icon {
      display: none;
    }
  }

  @media (max-width: 1750px) {
    width: 102%;
    #star-icon {
      display: none;
    }
  }
`;
